<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import type { FormFieldDef } from '@/types/page'
import type { RoleCode } from '@/types/admin'
import { pageById } from '@/data/registry'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { ADMIN_ROLES, ROLE_PERMISSIONS } from '@/data/roles'
import { adapter } from '@/services/prototype-adapter'
import { request } from '@/services/request'
import { ApiError } from '@/utils/errors'
import { formatDateTime } from '@/utils/format'
import AppIcon from '@/components/AppIcon.vue'
import ProSelect from '@/components/pro/ProSelect.vue'
import ProField from '@/components/pro/ProField.vue'

const definition = pageById('profile')
if (!definition) throw new Error('个人中心页面定义缺失')

const auth = useAuthStore()
const ui = useUiStore()
const canEdit = computed(() => auth.has('profile.edit'))
const saving = ref(false)
const errors = reactive<Record<string, string>>({})
const lastSavedAt = ref('')
const snapshot = ref('')
const activeTab = ref<'profile' | 'security' | 'notifications' | 'sessions'>('profile')
const noticePrefs = reactive({ alert: true, task: true, ticket: true, digest: false })
const sessions = ref([
  { id: 'current', device: 'Chrome · macOS', location: '上海市 · 10.24.18.32', issuedAt: formatDateTime(new Date()), current: true },
  { id: 'mobile', device: 'Safari · iPhone', location: '上海市 · 10.24.18.44', issuedAt: formatDateTime(new Date(Date.now() - 2 * 3_600_000)), current: false },
  { id: 'tablet', device: 'Edge · iPad', location: '杭州市 · 10.24.19.21', issuedAt: formatDateTime(new Date(Date.now() - 26 * 3_600_000)), current: false },
])
const securityForm = reactive({ currentPassword: '', newPassword: '', confirmPassword: '' })
const securityErrors = reactive<Record<string, string>>({})

const form = reactive({
  name: '',
  phone: '',
  email: '',
})

const fields: FormFieldDef[] = [
  { key: 'name', label: '姓名', type: 'text', required: true, maxLength: 20 },
  { key: 'phone', label: '手机号', type: 'phone', required: true, pattern: '^1[3-9]\\d{9}$', patternMessage: '请输入11位有效手机号' },
  { key: 'email', label: '邮箱', type: 'email', required: true, pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$', patternMessage: '请输入有效邮箱' },
]

const roleOptions = computed(() => ADMIN_ROLES.map(role => ({ value: role.code, label: role.name })))
const currentRole = computed(() => ADMIN_ROLES.find(role => role.code === auth.effectiveRole))
const permissionCount = computed(() => auth.permissions.length)

function applyProfile(): void {
  const profile = auth.profile
  if (!profile) return
  form.name = profile.name
  form.phone = profile.phone
  form.email = profile.email
  snapshot.value = JSON.stringify(form)
}

const dirty = computed(() => JSON.stringify(form) !== snapshot.value)

function beforeUnload(event: BeforeUnloadEvent): void {
  if (!dirty.value) return
  event.preventDefault()
  event.returnValue = ''
}

onBeforeRouteLeave(async () => {
  if (!dirty.value) return true
  try {
    await ElMessageBox.confirm('个人资料有未保存的修改，离开将放弃这些内容。', '放弃未保存的修改', {
      type: 'warning',
      confirmButtonText: '放弃修改',
      cancelButtonText: '继续编辑',
      distinguishCancelAndClose: true,
    })
    return true
  } catch {
    return false
  }
})

function validate(): boolean {
  for (const key of Object.keys(errors)) delete errors[key]
  if (!form.name.trim()) errors.name = '请填写姓名'
  if (!form.phone.trim()) errors.phone = '请填写手机号'
  else if (!/^1[3-9]\d{9}$/.test(form.phone)) errors.phone = '请输入11位有效手机号'
  if (!form.email.trim()) errors.email = '请填写邮箱'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = '请输入有效邮箱'
  return Object.keys(errors).length === 0
}

async function saveProfile(): Promise<void> {
  if (!canEdit.value || saving.value || !validate()) return
  saving.value = true
  try {
    await request(
      () => {
        auth.refreshProfile({ name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim() })
        return true
      },
      { write: true, delay: 380, useSimulation: true },
    )
    snapshot.value = JSON.stringify(form)
    lastSavedAt.value = formatDateTime(new Date())
    ElMessage.success('个人资料已保存到当前原型会话')
  } catch (error) {
    if (error instanceof ApiError && error.fieldErrors) Object.assign(errors, error.fieldErrors)
    ElMessage.error(error instanceof Error ? error.message : '保存失败，请重试')
  } finally {
    saving.value = false
  }
}

async function changePassword(): Promise<void> {
  if (!canEdit.value) return
  for (const key of Object.keys(securityErrors)) delete securityErrors[key]
  if (!securityForm.currentPassword) securityErrors.currentPassword = '请输入当前口令'
  if (securityForm.newPassword.length < 8) securityErrors.newPassword = '新口令至少8位'
  if (!/[A-Za-z]/.test(securityForm.newPassword) || !/\d/.test(securityForm.newPassword)) securityErrors.newPassword = '新口令需同时包含字母和数字'
  if (securityForm.newPassword !== securityForm.confirmPassword) securityErrors.confirmPassword = '两次输入的新口令不一致'
  if (Object.keys(securityErrors).length) return
  try {
    await request(
      () => {
        adapter.table('audit_logs').unshift({
          id: `audit-password-${Date.now()}`,
          actor_name: auth.profile?.name ?? '管理员',
          actor_account: auth.profile?.account ?? 'system',
          module: '个人中心',
          action: '修改口令',
          resource_label: '本人账号',
          result: '成功',
          ip: '10.24.18.32',
          request_no: `REQ-${Date.now()}`,
          detail: '管理员在原型环境修改登录口令（不会写入真实凭据）',
          created_at: new Date().toISOString(),
          row_version: 1,
        })
        securityForm.currentPassword = ''
        securityForm.newPassword = ''
        securityForm.confirmPassword = ''
        return true
      },
      { write: true, delay: 420, useSimulation: true },
    )
    ElMessage.success('口令已更新，其他会话已要求重新登录（原型提示）')
  } catch (error) {
    if (error instanceof ApiError && error.fieldErrors) Object.assign(securityErrors, error.fieldErrors)
    ElMessage.error(error instanceof Error ? error.message : '口令修改失败')
  }
}

function previewRole(role: RoleCode | null): void {
  auth.switchViewAs(role)
  ElMessage.info(role ? `已切换到${auth.roleName}视角，仅影响前端菜单与按钮` : '已退出角色视角预览')
}

async function revokeOthers(): Promise<void> {
  try {
    await ElMessageBox.confirm('将断开除当前设备外的其他会话，下一次请求需要重新登录。', '退出其他会话', {
      type: 'warning',
      confirmButtonText: '确认退出',
      cancelButtonText: '取消',
    })
  } catch {
    return
  }
  sessions.value = sessions.value.filter(session => session.current)
  ElMessage.success('其他会话已退出（原型模拟）')
}

function saveNoticePrefs(): void {
  ElMessage.success('通知偏好已保存到当前原型会话')
}

onMounted(() => {
  applyProfile()
  lastSavedAt.value = auth.session?.issuedAt ? formatDateTime(new Date(auth.session.issuedAt)) : ''
  window.addEventListener('beforeunload', beforeUnload)
})

onBeforeUnmount(() => window.removeEventListener('beforeunload', beforeUnload))
</script>

<template>
  <section class="profile-page">
    <header class="page-header">
      <div class="page-header__text">
        <div class="page-header__title-row"><h1 class="page-title">个人中心</h1><span class="prototype-pill">原型数据</span></div>
        <p class="page-subtitle">{{ definition.purpose }}</p>
      </div>
      <div class="page-header__actions">
        <button type="button" class="btn" @click="ui.rolePreviewOpen = true"><AppIcon name="eye" :size="14" />角色视角预览</button>
        <button type="button" class="btn btn--primary" :disabled="!canEdit || saving" @click="saveProfile">
          <span v-if="saving" class="button-spinner" />{{ saving ? '保存中…' : '保存资料' }}
        </button>
      </div>
    </header>

    <div class="profile-hero">
      <span class="avatar">{{ auth.profile?.name?.slice(-2) || '管理员' }}</span>
      <div class="profile-hero__main">
        <h1>{{ auth.profile?.name || '未登录' }}</h1>
        <div class="profile-hero__meta">
          <span>{{ auth.profile?.account }}</span>
          <span>{{ auth.profile?.adminNo }}</span>
          <span>{{ auth.roleName }}</span>
          <span>{{ auth.profile?.organization }}</span>
        </div>
        <div class="profile-hero__meta">
          <span>权限点 {{ permissionCount }} 项</span>
          <span>当前会话签发 {{ formatDateTime(auth.session?.issuedAt) }}</span>
          <span>过期 {{ formatDateTime(auth.session?.expiresAt) }}</span>
        </div>
      </div>
      <div class="profile-hero__side">
        <span class="tag" :class="auth.isViewingAs ? 'tag--warning' : 'tag--success'">{{ auth.isViewingAs ? '视角预览中' : '主身份' }}</span>
      </div>
    </div>

    <div class="segmented" style="margin: 14px 0" role="tablist" aria-label="个人中心分区">
      <button v-for="tab in [{ key: 'profile', label: '基本资料' }, { key: 'security', label: '安全设置' }, { key: 'notifications', label: '通知偏好' }, { key: 'sessions', label: '登录会话' }]" :key="tab.key" type="button" class="segmented__item" :class="{ 'segmented__item--active': activeTab === tab.key }" @click="activeTab = tab.key as 'profile' | 'security' | 'notifications' | 'sessions'">{{ tab.label }}</button>
    </div>

    <div class="settings-layout">
      <div>
        <section v-show="activeTab === 'profile'" class="settings-section card">
          <div class="settings-section__head">
            <div><h2 class="settings-section__title">基本资料</h2><p class="settings-section__sub">用于操作日志、任务指派与客服工单处理人展示</p></div>
            <span class="tag tag--neutral">{{ canEdit ? '可编辑' : '只读' }}</span>
          </div>
          <div class="settings-section__body">
            <div class="settings-form-grid">
              <ProField v-model="form.name" :field="fields[0]!" :error="errors.name" :disabled="!canEdit" />
              <ProField v-model="form.phone" :field="fields[1]!" :error="errors.phone" :disabled="!canEdit" />
              <ProField v-model="form.email" :field="fields[2]!" :error="errors.email" :disabled="!canEdit" />
            </div>
            <p class="muted" style="margin-top: 10px">仅修改当前浏览器会话中的展示资料，不会写入真实管理员账号。最近保存：{{ lastSavedAt || '—' }}</p>
            <button type="button" class="text-button" style="margin-top: 6px" @click="activeTab = 'security'">登录口令请在“安全设置”中修改</button>
          </div>
        </section>

        <section v-show="activeTab === 'security'" class="settings-section card">
          <div class="settings-section__head">
            <div><h2 class="settings-section__title">修改登录口令</h2><p class="settings-section__sub">原型只校验格式并写入操作日志，不保存真实口令</p></div>
          </div>
          <div class="settings-section__body">
            <div class="settings-form-grid">
              <ProField v-model="securityForm.currentPassword" :field="{ key: 'currentPassword', label: '当前口令', type: 'password', required: true }" :error="securityErrors.currentPassword" :disabled="!canEdit" />
              <ProField v-model="securityForm.newPassword" :field="{ key: 'newPassword', label: '新口令', type: 'password', required: true, help: '至少8位，包含字母和数字' }" :error="securityErrors.newPassword" :disabled="!canEdit" />
              <ProField v-model="securityForm.confirmPassword" :field="{ key: 'confirmPassword', label: '确认新口令', type: 'password', required: true }" :error="securityErrors.confirmPassword" :disabled="!canEdit" />
            </div>
            <div class="page-toolbar" style="margin-top: 10px">
              <button type="button" class="btn btn--primary" :disabled="!canEdit" @click="changePassword">更新口令</button>
              <button type="button" class="btn" @click="revokeOthers">退出其他会话</button>
            </div>
            <div class="info-banner info-banner--brand" style="margin-top: 10px"><AppIcon name="shield" :size="14" /><span>正式环境的口令修改必须由服务端验证当前口令、撤销旧会话并写入安全审计。</span></div>
          </div>
        </section>

        <section v-show="activeTab === 'notifications'" class="settings-section card">
          <div class="settings-section__head">
            <div><h2 class="settings-section__title">通知偏好</h2><p class="settings-section__sub">只影响当前原型的通知中心展示</p></div>
          </div>
          <div class="settings-section__body">
            <label class="checkbox-line"><el-switch v-model="noticePrefs.alert" /><span>健康预警与 SLA 提醒</span></label>
            <label class="checkbox-line" style="margin-top: 8px"><el-switch v-model="noticePrefs.task" /><span>异步任务完成与失败</span></label>
            <label class="checkbox-line" style="margin-top: 8px"><el-switch v-model="noticePrefs.ticket" /><span>客服工单指派与回复</span></label>
            <label class="checkbox-line" style="margin-top: 8px"><el-switch v-model="noticePrefs.digest" /><span>每日运营摘要</span></label>
            <button type="button" class="btn btn--primary" style="margin-top: 12px" @click="saveNoticePrefs">保存通知偏好</button>
          </div>
        </section>

        <section v-show="activeTab === 'sessions'" class="settings-section card">
          <div class="settings-section__head">
            <div><h2 class="settings-section__title">登录会话</h2><p class="settings-section__sub">当前浏览器原型会话与模拟设备</p></div>
            <button type="button" class="btn btn--sm" @click="revokeOthers">退出其他会话</button>
          </div>
          <div class="settings-section__body">
            <div v-for="session in sessions" :key="session.id" class="list-row">
              <div class="list-row__main">
                <div class="list-row__title">{{ session.device }} <span v-if="session.current" class="tag tag--success">当前</span></div>
                <div class="list-row__meta">{{ session.location }} · 签发于 {{ session.issuedAt }}</div>
              </div>
              <span class="tag" :class="session.current ? 'tag--success' : 'tag--neutral'">{{ session.current ? '使用中' : '其他设备' }}</span>
            </div>
          </div>
        </section>
      </div>

      <aside class="card card--pad settings-nav">
        <div class="settings-nav__item is-active"><AppIcon name="user" :size="14" />当前身份</div>
        <p style="margin: 6px 8px 12px"><b>{{ auth.profile?.name }}</b><br /><span class="faint">{{ currentRole?.description }}</span></p>
        <div class="divider" style="margin: 8px 0" />
        <div class="settings-nav__item"><AppIcon name="key" :size="14" />权限点 {{ permissionCount }}</div>
        <div class="settings-nav__item"><AppIcon name="lock" :size="14" />数据范围 {{ currentRole?.dataScopeLabel }}</div>
        <div v-if="auth.canPreviewRoles" style="margin-top: 10px">
          <label class="pro-field__label">角色视角预览</label>
          <ProSelect
            :model-value="auth.viewAsRole ?? 'none'"
            :options="[{ value: 'none', label: '按真实身份' }, ...roleOptions]"
            :clearable="false"
            aria-label="角色视角预览"
            @update:model-value="previewRole(String($event) === 'none' ? null : String($event) as RoleCode)"
          />
          <p class="faint" style="margin-top: 6px; line-height: 1.5">仅超级管理员可预览其他角色的菜单与按钮可见性，不会改变真实授权。</p>
        </div>
      </aside>
    </div>
  </section>
</template>

<style scoped>
.profile-hero__side { margin-left: auto; }
.button-spinner { width: 12px; height: 12px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: spin .8s linear infinite; }
</style>
